import { Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { DxDataGridComponent } from 'devextreme-angular';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';

import { ComponentIcons } from '../../../../ui/app-components.icons';
import { DcIcons } from '../../../../ui/app-dc.icons';
import { DcLoaders } from '../../../../ui/app-loaders';
import { ExpositionsSelector } from '../../../store';
import { ExpositionAccessListEntity } from './exposition-access-list.entity';
import { AccessType, ExpositionAccessEntity } from './exposition-access.entity';

interface OpenedOverlayId {
	columns: string | null;
	users: string | null;
	groups: string | null;
	lines: string | null;
}
@Component({
	selector: 'app-exposition-access-list',
	templateUrl: './exposition-access-list.component.html',
	styleUrls: ['./exposition-access-list.component.scss'],
	inputs: ['expositionAccessList'],
	exportAs: 'AccessPointsGrid',
})
export class ExpositionAccessListComponent extends DcBaseComponent {
	public ComponentIcons = ComponentIcons;
	protected readonly DcLoaders = DcLoaders;

	public openedOverlayIdsSubject = new BehaviorSubject<OpenedOverlayId>({
		groups: null,
		columns: null,
		lines: null,
		users: null,
	});
	public isOverlayContentLoadingSubject = new BehaviorSubject<boolean>(false);
	public vo$: Observable<{
		isFirstLoad: boolean;
		list: ExpositionAccessListEntity;
		openedOverlays: OpenedOverlayId;
		isOverlayContentLoading: boolean;
		canEdit: boolean;
	}>;
	protected readonly DcIcons = DcIcons;

	protected readonly AccessType = AccessType;

	@ViewChild(DxDataGridComponent)
	private readonly grid: DxDataGridComponent | null = null;

	private readonly firstLoadSubject = new BehaviorSubject<boolean>(true);

	@Output()
	public editAccess = new EventEmitter<string>();

	@Output()
	public toggleAccessStatus = new EventEmitter<string>();

	@Output()
	public deleteAccess = new EventEmitter<ExpositionAccessEntity>();

	public constructor(
		private readonly expositionsSelector: ExpositionsSelector
	) {
		super();
		this.cmpId = 'exposition-access-list';
		this.dxLocalStorageKey = 'dx.grid.exposition:access-list';

		this.vo$ = combineAll({
			isFirstLoad: this.firstLoadSubject,
			list: this.toObservable<ExpositionAccessListEntity>(
				'expositionAccessList'
			),
			openedOverlays: this.openedOverlayIdsSubject,
			isOverlayContentLoading: this.isOverlayContentLoadingSubject,
			canEdit: this.expositionsSelector.getCanEditExposition$(),
		});
	}

	public closeOverlay(): void {
		this.isOverlayContentLoadingSubject.next(false);
		this.openedOverlayIdsSubject.next({
			groups: null,
			columns: null,
			lines: null,
			users: null,
		});
	}

	public async openColumnsOverlay(
		entry: ExpositionAccessEntity
	): Promise<void> {
		const { id, columnsCount } = entry;
		if (!columnsCount) {
			return;
		}
		const openedIds = await firstValueFrom(this.openedOverlayIdsSubject);
		if (id === '' || id === openedIds.columns) {
			this.isOverlayContentLoadingSubject.next(false);
			this.openedOverlayIdsSubject.next({
				...openedIds,
				columns: null,
			});
		} else {
			this.openedOverlayIdsSubject.next({
				users: null,
				groups: null,
				lines: null,
				columns: id,
			});
		}
	}

	public async openUsersOverlay(entry: ExpositionAccessEntity): Promise<void> {
		const { id, usersCount } = entry;
		if (!usersCount) {
			return;
		}
		const openedIds = await firstValueFrom(this.openedOverlayIdsSubject);
		if (id === '' || id === openedIds.users) {
			this.openedOverlayIdsSubject.next({
				...openedIds,
				users: null,
			});
		} else {
			this.openedOverlayIdsSubject.next({
				lines: null,
				groups: null,
				columns: null,
				users: id,
			});
		}
	}

	public async openGroupsOverlay(entry: ExpositionAccessEntity): Promise<void> {
		const { id, groupsCount } = entry;
		if (!groupsCount) {
			return;
		}
		const openedIds = await firstValueFrom(this.openedOverlayIdsSubject);
		if (id === '' || id === openedIds.groups) {
			this.openedOverlayIdsSubject.next({
				...openedIds,
				groups: null,
			});
		} else {
			this.openedOverlayIdsSubject.next({
				users: null,
				lines: null,
				columns: null,
				groups: id,
			});
		}
	}

	public async openLinesOverlay(entry: ExpositionAccessEntity): Promise<void> {
		const { id, lines } = entry;
		if (!lines) {
			return;
		}
		const openedIds = await firstValueFrom(this.openedOverlayIdsSubject);
		if (id === '' || id === openedIds.lines) {
			this.openedOverlayIdsSubject.next({
				...openedIds,
				lines: null,
			});
		} else {
			this.openedOverlayIdsSubject.next({
				users: null,
				groups: null,
				columns: null,
				lines: id,
			});
		}
	}

	public goToAccessConfig(id: string): void {
		this.editAccess.emit(id);
	}

	public toggleStatus(id: string): void {
		this.toggleAccessStatus.emit(id);
	}

	public deleteExpositionAccess(access: ExpositionAccessEntity): void {
		this.deleteAccess.emit(access);
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		this.grid.instance.searchByText(searchTerm.trim());
	}

	public async onGridContentReady(): Promise<void> {
		const firstLaod = await firstValueFrom(this.firstLoadSubject);

		if (firstLaod) {
			this.firstLoadSubject.next(false);
		}
	}
}
